import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AppModal } from '../../shared/components/AppModal';
import { IconAlertCircle } from '@tabler/icons-react';
import { ApiError } from '../../shared/api/problem';
import { useBalanceTypes, useLocationTypes } from '../lookups/useLookups';
import { useWarehouses } from '../warehouses/useWarehouses';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import {
  LOCATION_CODE_MAX,
  LOCATION_NAME_MAX,
  createLocation,
  updateLocation,
  type LocationDto,
} from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** null/undefined = create; a location = edit. */
  location?: LocationDto | null;
  /** Pre-selects the warehouse when creating from a filtered list. */
  onSaved: () => void;
}

interface FormState {
  warehouseId: string;
  locationTypeId: string;
  locationCode: string;
  displayName: string;
  balanceTypeId: string;
  isForwardPick: boolean;
  /** Empty string means "no walk order", which is a real answer rather than zero. */
  sequence: string;
  isActive: boolean;
}

const EMPTY: FormState = {
  warehouseId: '',
  locationTypeId: '',
  locationCode: '',
  displayName: '',
  balanceTypeId: '',
  isForwardPick: false,
  sequence: '',
  isActive: true,
};

export function LocationFormModal({
  opened,
  onClose,
  location,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const isEdit = Boolean(location);

  const warehouses = useWarehouses();
  // Which warehouse a new location belongs to is not a choice: the API refuses a body naming
  // a warehouse other than the header. Shown all the same, because when you are naming a bin
  // it is worth seeing which building you are naming it in.
  const currentWarehouseId = useCurrentWarehouseId();
  const locationTypes = useLocationTypes();
  const balanceTypes = useBalanceTypes();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Only active warehouses can take new locations; an existing location's
  // warehouse stays selectable so editing never shows a blank picker.
  const warehouseOptions = useMemo(() => {
    const all = warehouses.data ?? [];
    return all
      .filter((w) => w.isActive || w.warehouseId === location?.warehouseId)
      .map((w) => ({ value: w.warehouseId, label: `${w.warehouseCode} — ${w.displayName}` }));
  }, [warehouses.data, location?.warehouseId]);

  const locationTypeOptions = useMemo(
    () =>
      (locationTypes.data ?? []).map((lt) => ({
        value: lt.locationTypeId,
        label: `${lt.locationTypeCode} — ${lt.displayName}`,
      })),
    [locationTypes.data],
  );

  const balanceTypeOptions = useMemo(
    () =>
      (balanceTypes.data ?? []).map((bt) => ({
        value: String(bt.balanceTypeId),
        label: `${bt.balanceTypeCode} — ${bt.displayName}`,
      })),
    [balanceTypes.data],
  );

  // Default the balance category to the first "available" one (normally
  // on-hand), which is what the API falls back to anyway.
  const defaultBalanceTypeId = useMemo(() => {
    const list = balanceTypes.data ?? [];
    const available = list.find((bt) => bt.isAvailable) ?? list[0];
    return available ? String(available.balanceTypeId) : '';
  }, [balanceTypes.data]);

  useEffect(() => {
    if (!opened) return;
    setErrors({});
    setSubmitError(null);
    setForm(
      location
        ? {
            warehouseId: location.warehouseId,
            locationTypeId: location.locationTypeId,
            locationCode: location.locationCode,
            displayName: location.displayName ?? '',
            balanceTypeId: String(location.balanceTypeId),
            isForwardPick: location.isForwardPick,
            sequence: location.sequence === null ? '' : String(location.sequence),
            isActive: location.isActive,
          }
        : {
            ...EMPTY,
            warehouseId: currentWarehouseId,
            balanceTypeId: defaultBalanceTypeId,
          },
    );
  }, [opened, location, currentWarehouseId, defaultBalanceTypeId]);

  const mutation = useMutation({
    mutationFn: () => {
      const displayName = form.displayName.trim() || null;
      const balanceTypeId = Number(form.balanceTypeId);
      // Blank means "no walk order", which the server stores as NULL and sorts last.
      // Number('') is 0, and 0 is a perfectly good FIRST position — so the empty case
      // has to be spelled out rather than left to a cast.
      const sequence = form.sequence.trim() === '' ? null : Number(form.sequence);

      return isEdit && location
        ? updateLocation(location.locationId, {
            locationTypeId: form.locationTypeId,
            displayName,
            balanceTypeId,
            isForwardPick: form.isForwardPick,
            sequence,
            isActive: form.isActive,
          })
        : createLocation({
            warehouseId: form.warehouseId,
            locationTypeId: form.locationTypeId,
            locationCode: form.locationCode.trim(),
            displayName,
            balanceTypeId,
            isForwardPick: form.isForwardPick,
            sequence,
            isActive: form.isActive,
          });
    },
    onSuccess: () => {
      notifications.show({
        message: isEdit ? t('locations.updated') : t('locations.created'),
        color: 'green',
      });
      onSaved();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setErrors((e) => ({ ...e, locationCode: t('locations.errors.codeExists') }));
        return;
      }
      setSubmitError(t('locations.errors.saveFailed'));
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const next: typeof errors = {};
    if (!isEdit && !form.warehouseId) next.warehouseId = t('locations.errors.warehouseRequired');
    if (!isEdit && !form.locationCode.trim()) next.locationCode = t('locations.errors.codeRequired');
    if (!form.locationTypeId) next.locationTypeId = t('locations.errors.typeRequired');
    if (!form.balanceTypeId) next.balanceTypeId = t('locations.errors.balanceTypeRequired');
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    mutation.mutate();
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('locations.editTitle') : t('locations.createTitle')}
      width="md"
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Select
              label={t('locations.warehouse')}
              placeholder={t('locations.warehousePlaceholder')}
              data={warehouseOptions}
              value={form.warehouseId || null}
              onChange={(v) => setForm({ ...form, warehouseId: v ?? '' })}
              error={errors.warehouseId}
              description={t('locations.warehouseImmutable')}
              disabled
              searchable
              allowDeselect={false}
              nothingFoundMessage={t('shell.noMatch')}
              required={!isEdit}
            />
            <TextInput
              label={t('locations.code')}
              value={form.locationCode}
              onChange={(e) => setForm({ ...form, locationCode: e.currentTarget.value })}
              error={errors.locationCode}
              disabled={isEdit}
              description={isEdit ? t('locations.codeImmutable') : t('locations.codeHint')}
              maxLength={LOCATION_CODE_MAX}
              required={!isEdit}
              autoFocus={!isEdit}
            />
          </SimpleGrid>

          <TextInput
            label={t('locations.name')}
            description={t('locations.nameOptional')}
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.currentTarget.value })}
            maxLength={LOCATION_NAME_MAX}
            autoFocus={isEdit}
          />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Select
              label={t('locations.locationType')}
              placeholder={t('locations.locationTypePlaceholder')}
              data={locationTypeOptions}
              value={form.locationTypeId || null}
              onChange={(v) => setForm({ ...form, locationTypeId: v ?? '' })}
              error={errors.locationTypeId}
              searchable
              allowDeselect={false}
              nothingFoundMessage={t('shell.noMatch')}
              disabled={locationTypes.isLoading}
              required
            />
            <Select
              label={t('locations.balanceType')}
              placeholder={t('locations.balanceTypePlaceholder')}
              description={t('locations.balanceTypeHint')}
              data={balanceTypeOptions}
              value={form.balanceTypeId || null}
              onChange={(v) => setForm({ ...form, balanceTypeId: v ?? '' })}
              error={errors.balanceTypeId}
              allowDeselect={false}
              disabled={balanceTypes.isLoading}
              required
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Stack gap={4}>
              <Switch
                label={t('locations.isForwardPick')}
                checked={form.isForwardPick}
                onChange={(e) => setForm({ ...form, isForwardPick: e.currentTarget.checked })}
              />
              <Text size="xs" c="dimmed">
                {t('locations.forwardPickHint')}
              </Text>
            </Stack>
            {/*
              Walk order (065). Beside the forward-pick switch because they are one decision:
              this location is on the route, and this is where on it. Left blank the location is
              still pickable, it is simply walked last.
            */}
            <NumberInput
              label={t('locations.sequence')}
              description={t('locations.sequenceHint')}
              placeholder={t('locations.sequenceUnset')}
              value={form.sequence === '' ? '' : Number(form.sequence)}
              onChange={(v) => setForm({ ...form, sequence: v === '' ? '' : String(v) })}
              min={0}
              step={10}
              allowDecimal={false}
              allowNegative={false}
            />
          </SimpleGrid>

          <Switch
            label={t('locations.active')}
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })}
          />

          {submitError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {submitError}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {t('shell.cancel')}
            </Button>
            <Button type="submit" color="invetrak" loading={mutation.isPending}>
              {isEdit ? t('shell.saveChanges') : t('locations.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}
